import { memo, useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useUpdateForgotPassword } from 'hooks/UseUpdateForgotPassword';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/ForgotPasswordValidator';
import { CompleteDialog } from 'components/organisms/CompleteDialog';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress } from '@material-ui/core';
import { convertObjectToGetParameterWithEncode } from 'common/ObjectToGetParameterConverter';
import { useStore } from 'store/StoreContext';
import {
	MESSAGE_PASSWORD_ANNOTATION,
	POPOVER_MESSAGE_PASSWORD_ANNOTATION,
	TARGET_MESSAGE_PASSWORD_POPOVER,
} from 'utils/messages';
import { PartialdAnnotation } from 'components/molecules/partial-annotation/PartialAnnotation';

import classes from './override.module.scss';

type Props = {};

export type Passwords = {
	newPassword: string;
	newPasswordConfirm: string;
};

export const ForgotPasswordUpdateTemplate = memo((_props: Props) => {
	const history = useHistory();
	// 認証時のgidを取得
	const location = useLocation<{ confirmGid: string }>();
	const [store, dispatch] = useStore(); // eslint-disable-line
	const [formData, setFormData] = useState<Passwords>({ newPassword: '', newPasswordConfirm: '' });
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const handleDialogOpen = () => {
		setIsDialogOpen(true);
	};
	const handleDialogClose = () => {
		setIsDialogOpen(false);
		if (!!store.redirectLoginData.redirect_uri) {
			const getParameters = convertObjectToGetParameterWithEncode(store.redirectLoginData, 'redirect_uri', 'client_id');
			history.push(`${RoutePath.REDIRECT_LOGIN}?${getParameters}`);
		} else {
			history.push(RoutePath.LOGIN);
		}
	};
	const [processLoading, setProcessLoading] = useState(false);
	const { update } = useUpdateForgotPassword(
		formErrors,
		setFormErrors,
		getErrorMessage,
		actionError,
		setActionError,
		formData,
		handleDialogOpen,
		setProcessLoading,
		location.state.confirmGid,
	);

	// パスワード注釈
	const splitText = new RegExp('(' + TARGET_MESSAGE_PASSWORD_POPOVER + ')');
	const annotationMessage = MESSAGE_PASSWORD_ANNOTATION.split(splitText);

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({ ...formData, [event.target.name]: event.target.value });
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDパスワード忘れ｜パスワード更新';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<>
			<section className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="section-head">パスワード忘れ</div>
					<div className="section-txt-box">
						<p>新しいパスワードを入力してください</p>
					</div>
					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.newPassword !== '' ? <p>【新しいパスワード】{formErrors.newPassword}</p> : null}
										{formErrors.newPasswordConfirm !== '' ? (
											<p>【新しいパスワードの確認】{formErrors.newPasswordConfirm}</p>
										) : null}
									</div>
								</>
							) : null}
						</div>
					) : null}
					<div className="form-box">
						<div className={`form-head ${classes.displayFlex}`}>
							<div className={classes.newPasswordLabelBox}>
								<div className={classes.newPasswordLabel}>
									<label htmlFor="password">新しいパスワード</label>
									<span className="required">必須</span>
								</div>
							</div>

							<div>
								<span className="annotation">
									<PartialdAnnotation
										headerMessage={annotationMessage[0]}
										footerMessage={annotationMessage[2]}
										targetMessage={annotationMessage[1]}
										popoverMessage={POPOVER_MESSAGE_PASSWORD_ANNOTATION}
										isBreakBeforeTargetText={true}
									/>
								</span>
							</div>
						</div>
						<div className="form-input width50">
							<input type="password" name="newPassword" value={formData.newPassword} onChange={handleChangeValue} />
						</div>
						<p className="error">{formErrors.newPassword}</p>
					</div>
					<div className="form-box">
						<div className="form-head">
							<label htmlFor="">新しいパスワードの確認</label>
							<span className="required">必須</span>
						</div>
						<div className="form-input width50">
							<input
								type="password"
								name="newPasswordConfirm"
								value={formData.newPasswordConfirm}
								onChange={handleChangeValue}
							/>
						</div>
						<p className="error">{formErrors.newPasswordConfirm}</p>
					</div>

					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? <CircularProgress /> : <button onClick={update}>パスワードを更新する</button>}
						</div>
					</div>
				</div>
			</section>
			{/* 完了ダイアログ */}
			<CompleteDialog isOpen={isDialogOpen} doTrans={handleDialogClose} buttonLabel="ログイン画面" />
		</>
	);
});
